import React from 'react';
import { useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { addMonths, differenceInCalendarMonths } from 'helpers/dates';
import queryString from 'query-string';

import PageHeader from 'components/general/pageHeader';
import SortFilter from 'components/general/sortFilter';
import TextClamp from 'components/textClamp';
import UserMenu from 'components/user/menu';
import {
    DomHelpers,
    FloatingMenu,
    Help,
    Loading,
    SystemText,
    Box,
    Copy,
    ArrayHelpers,
    DebugInfo,
} from '@salesperformanceint/e4shared';

import CRMGoals from './components/crmGoals';
import Competencies from './components/competencies';
import IntegrationMetrics from './components/integrationMetrics';
import CRMMetrics from './components/crmMetrics';
import Notes from './components/notes';

import { apiDelete, apiGet } from 'services';

import './user121s.css';
import { coachRoles, hasModule, isCoach } from 'helpers/profile';
import { GetIntegrationMetricName } from 'components/admin/forms/metricPicker';
import EditGoals from './components/editGoals';
import { getPermissions } from 'helpers/permissions';
import PredictiveModelWrapper from './components/predictiveModelWrapper';
import posthog from 'posthog-js';
import PerformanceDashboard from './components/performanceDashboard';

export const limitOverTimeData = (data, dateProp = 'date_Set', valueProp = 'value', minMonths = 13, maxMonths = 18) => {
    const now = new Date();
    const monthsArray = new Array(minMonths + 1).fill(null).map((c, i) => {
        return i;
    });

    monthsArray.forEach(k => {
        if (!data.some(d => differenceInCalendarMonths(now, d[dateProp]) === k)) {
            let realMonth = addMonths(now, k * -1);
            let year = realMonth.getFullYear();
            let month = realMonth.getMonth();
            const newMonth = {};
            newMonth[dateProp] = new Date(year, month, 1).toISOString();
            data.push(newMonth);
        }
    });

    data = data.sort((a, b) => (a[dateProp] > b[dateProp] ? 1 : -1));

    const result = data.filter(d => differenceInCalendarMonths(now, d[dateProp]) <= maxMonths);
    if (ArrayHelpers.existsAndHasLength(result)) {
        const first = result[0];
        if (typeof first[valueProp] === 'undefined') {
            const earlier = data.filter(d => d[dateProp] < first[dateProp] && typeof d[valueProp] !== 'undefined');
            if (earlier?.length) {
                first[valueProp] = earlier[earlier.length - 1][valueProp];
            }
        }
    }

    return result.filter(r => typeof r[valueProp] !== 'undefined');
};

export const User121s = ({ match, history }) => {
    const [timeTracker, setTimeTracker] = React.useState(null);
    const [error, setError] = React.useState(false);
    const [currentMetric, setCurrentMetric] = React.useState(null);
    const [range, setRange] = React.useState('month');
    const [offset, setOffset] = React.useState(0);
    const [benchmark, setBenchmark] = React.useState(0);
    const [permissions, setPermissions] = React.useState(getPermissions());
    const [profile, setProfile] = React.useState(null);
    const [notes, setNotes] = React.useState(null);
    const [metrics, setMetrics] = React.useState(null);
    const [extraMetrics, setExtraMetrics] = React.useState(null);
    const [integrationMetrics, setIntegrationMetrics] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [loadingCRM, setLoadingCRM] = React.useState(false);
    const [loadingRealtime, setLoadingRealtime] = React.useState(false);
    const [loadingInt, setLoadingInt] = React.useState(false);
    const [loadedCRM, setLoadedCRM] = React.useState(false);
    const [loadedRealtime, setLoadedRealtime] = React.useState(false);
    const [loadedInt, setLoadedInt] = React.useState(false);
    const [loadedProfile, setLoadedProfile] = React.useState(false);
    const [filteredCompetencies, setFilteredCompetencies] = React.useState(null);
    const [filteredMetric, setFilteredMetric] = React.useState(null);
    const [realtimeMetrics, setRealtimeMetrics] = React.useState([]);
    const [zero, setZero] = React.useState(false);
    const [hasIntegration, setHasIntegration] = React.useState(false);
    const [hasGoal, setHasGoal] = React.useState(true);
    const [lastPaint, setLastPaint] = React.useState(null);
    const [showEditPersonalGoals, setShowEditPersonalGoals] = React.useState(false);
    const [showModel, setShowModel] = React.useState(false);

    const didMountRef = React.useRef(false);
    const topRowRef = React.useRef(false);

    const { coaching, downline, user } = useSelector(state => state.user);

    const { showNotesOnPerformanceDashboard, usePerformanceDashboard } = user.companySettings?.development;

    const id = match.params.id;
    const isMe = user && profile && user.id === profile.id;
    const hasCRM = hasModule(user, 'integrations') && user.crmIntegration;

    const integrations = user.integrations
        ?.map(i => ({ ...i, name: GetIntegrationMetricName(i, user) }))
        .sort((a, b) => (a.name > b.name ? 1 : -1));

    React.useEffect(() => {
        const temp = hasModule(user, 'integrations') && ArrayHelpers.existsAndHasLength(integrations);
        setHasIntegration(temp);
    }, [user]);

    React.useEffect(() => {
        if (profile?.id && user?.id) {
            setPermissions(getPermissions(profile, user, coaching, downline));
        }
    }, [profile?.id, user?.id]);

    const loadCrmMetrics = () => {
        if (usePerformanceDashboard) return;
        if (id) {
            setLoadingCRM(true);
            setLoadingRealtime(true);

            apiGet(`profile/${id}/crm-metrics/${benchmark}?range=${range}&offset=${offset}`, user.guid, response => {
                setMetrics(response.metrics ? response.metrics : []);
                setLoadedCRM(new Date());
                setLoadingCRM(false);
            });

            apiGet(`profile/${id}/realtime-metrics/${benchmark}?range=${range}&offset=${offset}`, user.guid, response => {
                setExtraMetrics(response.extraMetrics ? response.extraMetrics : []);
                setLoadedRealtime(new Date());
                setLoadingRealtime(false);
            });
        }
    };

    const loadMetrics = () => {
        if (usePerformanceDashboard) return;
        if (id && integrations?.length) {
            setLoadingInt(true);
            apiGet(
                `profile/${id}/integration-metrics/${benchmark}?range=${range}&offset=${offset}`,
                user.guid,
                response => {
                    setIntegrationMetrics(response);
                    setLoadedInt(true);
                    setLoadingInt(false);
                },
                () => setError(true)
            );
        } else {
            setLoadedInt(true);
        }
    };

    const loadOne2OneNotes = () => {
        if (!showNotesOnPerformanceDashboard) return;
        id &&
            apiGet(
                `profile/${id}/121notes`,
                user.guid,
                response => {
                    setNotes(response);
                },
                () => setError(true)
            );
    };

    const loadProfile = () => {
        id &&
            apiGet(
                `profile/${id}?extra=hasopp`,
                user.guid,
                response => {
                    setProfile(response);
                    setZero(response.licence === 'ZERO');
                    setHasGoal(response.hasGoals);
                    setLoadedProfile(true);
                },
                () => setError(true)
            );
    };

    const deleteNote = note => {
        apiDelete(
            `profile/note/${note.id}`,
            null,
            user.guid,
            () => {
                loadOne2OneNotes();
            },
            () => setError(true)
        );
    };

    // TODO: Move takeWidthFromDirectParent to shared
    const truncateText = (className, parent, extra, takeWidthFromDirectParent, callback) => {
        const baseNode = parent ? parent : document;
        const elements = baseNode.getElementsByClassName(className);
        const defaultWidth = parent.clientWidth - extra;
        for (let i = 0; i < elements.length; i++) {
            const elm = elements[i];
            const width = takeWidthFromDirectParent ? elm.parentElement.clientWidth - extra : defaultWidth;
            elm.style.maxWidth = `${width}px`;
        }

        callback?.();
    };

    const setMetricBenchmarkWidths = id => {
        const parent = document.getElementById(id);
        const elms = parent.getElementsByClassName('progress');
        const marks = [...elms];
        marks.forEach(m => (m.style.width = null));
        const max = marks.reduce((a, c) => (c.clientWidth > a ? c.clientWidth : a), 0);
        marks.forEach(m => (m.style.width = `${max}px`));
    };

    const resizeAndFixWidths = () => {
        if (topRowRef.current && !timeTracker) {
            const temp = setTimeout(() => {
                const className = usePerformanceDashboard ? 'behaviour-sync' : 'box-body';
                DomHelpers.arrangeTiles(className, topRowRef.current);

                try {
                    setMetricBenchmarkWidths('121-metrics');

                    [...document.getElementsByClassName('ellipse')].forEach(x => (x.style.display = 'none'));
                    truncateText('ellipse', didMountRef.current, 30, true, () => {
                        [...document.getElementsByClassName('ellipse')].forEach(x => (x.style.display = 'inline-block'));
                    });
                } catch {
                    // No competencies or something
                }

                setTimeTracker(null);
            }, 100);

            setTimeTracker(temp);
        }
    };

    React.useEffect(() => {
        loadProfile();
        loadOne2OneNotes();
        !usePerformanceDashboard &&
            apiGet('all-fields', user.guid, response => setRealtimeMetrics(response.filter(x => x.realTime)));
        resizeAndFixWidths();

        window.addEventListener('resize', resizeAndFixWidths);

        return () => {
            window.removeEventListener('resize', resizeAndFixWidths);
        };
    }, []);

    React.useEffect(() => {
        setLoading(!loadedCRM || !loadedRealtime || !loadedInt || !loadedProfile);
    }, [loadedCRM, loadedRealtime, loadedInt, loadedProfile]);

    React.useEffect(() => {
        id && user && loadMetrics();
    }, [user, id, benchmark, range, offset]);

    React.useEffect(() => {
        setFilteredMetric(null);
        setFilteredCompetencies(null);
    }, [currentMetric]);

    React.useEffect(() => {
        id && user && loadCrmMetrics();
    }, [user, id, benchmark, range, offset]);

    React.useEffect(() => {
        if (!loading && !currentMetric) {
            if (!metrics?.length || !hasCRM) {
                const first =
                    hasIntegration &&
                    integrationMetrics &&
                    integrationMetrics.find(x => integrations.some(u => u.ref === x.ref))?.ref;
                setCurrentMetric(first || '');
            } else {
                setCurrentMetric('CRM');
            }
        }
    }, [loading, metrics, integrationMetrics]);

    React.useEffect(() => {
        if (didMountRef.current) {
            resizeAndFixWidths();
        }
    }, [didMountRef, topRowRef.current, lastPaint, loadedCRM, loadedRealtime, currentMetric]);

    React.useEffect(() => {
        DebugInfo('component', 'User121s');
    }, []);

    if (user && !match.params.id) history.replace(match.path.replace(':id?', user.id));
    if (error) history.replace('/error');

    const metricImage = metric => {
        switch (metric) {
            case 'CRM':
                return (
                    <img
                        alt=""
                        loading="lazy"
                        src={`/dist/img/${user.crmIntegration.replace(' ', '_')?.toLowerCase()}-icon.png`}
                    />
                );
            default:
                return <img alt="" loading="lazy" src={`/dist/img/${metric?.toLowerCase()}-icon.png`} />;
        }
    };

    const metricButton = (metric, name) => {
        switch (metric) {
            case 'CRM':
                return (
                    <div className="flex flex-center">
                        <div className="mr5">{metricImage('CRM')}</div>
                        <div className="mr5">{user.crmIntegration}</div>
                    </div>
                );
            default:
                return (
                    <div className="flex flex-center">
                        <div className="mr5">{metricImage(metric)}</div>
                        <div className="mr5">{name}</div>
                    </div>
                );
        }
    };

    let moduleCount = 0;
    if (hasModule(user, 'objectives')) moduleCount++;
    if (hasModule(user, 'blogs')) moduleCount++;
    if (hasModule(user, 'knowledge')) moduleCount++;

    const allMetrics = (metrics || []).concat(integrationMetrics || []).concat(realtimeMetrics || []);
    const hasMetrics = ((hasCRM || hasIntegration) && allMetrics?.length > 0) || (moduleCount > 1 && !zero);
    const { set } = queryString.parse(history.location.search);

    const splitCurrentMetric = current => {
        let metric = { ref: current };

        const intMetric = integrations.find(i => i.ref === current);

        metric = intMetric || metric;

        return { name: metric.name, ref: metric.ref };
    };

    const curr = splitCurrentMetric(currentMetric);

    const metricButtons = () => {
        return (
            <div className="mr10 mlm10 float-right float-image">
                <FloatingMenu buttonText={metricButton(curr.ref, curr.name)} small className="btn-anycase">
                    {hasCRM && metrics?.length > 0 && (
                        <li
                            onClick={() => {
                                setCurrentMetric('CRM');
                                posthog.capture('switched-metric-integration', {
                                    integration: 'CRM',
                                });
                            }}
                            className={`btn-icon ${currentMetric === 'CRM' ? 'selected' : ''}`}
                        >
                            {metricButton('CRM')}
                        </li>
                    )}
                    {hasIntegration &&
                        integrations.map(m => {
                            return !integrationMetrics.some(i => i.ref === m.ref) &&
                                !extraMetrics.some(i => i.ref === m.ref) ? null : (
                                <li
                                    key={`metric-${m.ref}`}
                                    onClick={() => {
                                        setCurrentMetric(m.ref);
                                        posthog.capture('switched-metric-integration', {
                                            integration: m.ref,
                                        });
                                    }}
                                    className={`btn-icon ${currentMetric === m.ref ? 'selected' : ''}`}
                                >
                                    {metricButton(m.ref, m.name)}
                                </li>
                            );
                        })}
                </FloatingMenu>
            </div>
        );
    };

    const sortButtons = () => (
        <div className="btn-row">
            <label>
                <Help id="benchmark" before>
                    <div>
                        {isMe
                            ? SystemText('User121/Benchmark/Help/You')
                            : SystemText('User121/Benchmark/Help/Them', profile?.full_Name)}
                    </div>
                </Help>
            </label>
            <button className={`btn btn-sm btn-${benchmark === 0 ? 'primary' : 'default'}`} onClick={() => setBenchmark(0)}>
                {SystemText('User121/Benchmark/Team')}
            </button>
            {profile?.roles &&
                profile?.roles.map(r => (
                    <button
                        key={`role-${r.id}`}
                        className={`btn btn-sm btn-${benchmark === r.id ? 'primary' : 'default'}`}
                        onClick={() => setBenchmark(r.id)}
                    >
                        {SystemText('User121/Benchmark/Role')} - {r.name}
                    </button>
                ))}
        </div>
    );

    const buttons = () => (
        <div className="flex">
            {profile?.hasOppGoals && (
                <button data-testid="button-show-modal" className="btn btn-primary" onClick={() => setShowModel(true)}>
                    <i className="fa fa-calculator mr10" />
                    {SystemText('User121/Buttons/Model')}
                </button>
            )}
            {profile && <UserMenu profile={profile} user={user} hide={'1-2-1s'} />}
        </div>
    );

    return (
        <>
            {usePerformanceDashboard ? (
                <>
                    <PerformanceDashboard profile={profile} buttons={buttons} />
                    {showNotesOnPerformanceDashboard && (
                        <div data-testid="121s-notes" className="col-md-12">
                            <Notes
                                permissions={permissions}
                                notes={notes}
                                profile={profile}
                                reload={loadOne2OneNotes}
                                deleteNote={deleting => deleteNote(deleting)}
                                outerHeader
                                bullets
                            />
                        </div>
                    )}
                </>
            ) : (
                <div data-testid="user121s" className="user121s" id="user121s" ref={didMountRef}>
                    <PageHeader
                        testId="121s-page-header"
                        title={SystemText('User121/Title')}
                        overrideProfile={profile ? profile : null}
                        extra={buttons}
                        homepage
                    />
                    <section className="content mtm10" data-testid="121-content">
                        <SortFilter
                            sortButtons={sortButtons}
                            sortText={SystemText('User121/Benchmark/Label')}
                            testId="121s-sort-filter"
                        />
                        {loading || !profile || !permissions.one2ones.canView ? (
                            <Loading />
                        ) : (
                            <div data-testid="121-content-loaded" className="row">
                                <div id="top-row" className="col-md-12" ref={topRowRef}>
                                    {(!zero || hasGoal) && (
                                        <div className="row">
                                            {!zero && (
                                                <div className={`col-md-${hasGoal ? 6 : 12}`}>
                                                    <Box
                                                        title={SystemText('User121/PersonalGoals/Title')}
                                                        button={() =>
                                                            !permissions.one2ones.canEditPersonalGoals ? null : (
                                                                <button
                                                                    data-testid="button-edit-personal-goal"
                                                                    className="btn btn-primary text-sm"
                                                                    onClick={() => setShowEditPersonalGoals(true)}
                                                                >
                                                                    {SystemText('User121/PersonalGoals/Button')}
                                                                </button>
                                                            )
                                                        }
                                                    >
                                                        <TextClamp lines="5" callback={resizeAndFixWidths}>
                                                            <div data-testid="user121-goals-box" className="goals">
                                                                <Copy
                                                                    value={
                                                                        profile.goals
                                                                            ? profile.goals
                                                                            : isMe
                                                                              ? SystemText('User121/PersonalGoals/None/You')
                                                                              : SystemText(
                                                                                    'User121/PersonalGoals/None/Them',
                                                                                    profile.first_Name
                                                                                )
                                                                    }
                                                                />
                                                            </div>
                                                        </TextClamp>
                                                    </Box>
                                                </div>
                                            )}
                                            {hasGoal && (
                                                <div className={`col-md-${!zero ? 6 : 12}`} id="121-goals">
                                                    <CRMGoals
                                                        testId="user121-CRMgoals-box"
                                                        setLastPaint={setLastPaint}
                                                        profile={profile}
                                                        onLoad={resizeAndFixWidths}
                                                        showSetGoals={set}
                                                        onNoGoals={() => setHasGoal(false)}
                                                        showPaging={false}
                                                        home={!permissions.one2ones.canEditMetricGoals}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div data-testid="121-metrics" id="121-metrics" className="col-md-6">
                                    {hasMetrics ? (
                                        <>
                                            {currentMetric === 'CRM' && (
                                                <CRMMetrics
                                                    testId="121s-crm-metrics"
                                                    setLastPaint={setLastPaint}
                                                    loading={loadingCRM}
                                                    extraButtons={metricButtons}
                                                    profile={profile}
                                                    metrics={metrics}
                                                    realtimeMetrics={realtimeMetrics}
                                                    extraMetrics={extraMetrics}
                                                    updateRange={x => setRange(x)}
                                                    updateOffset={x => setOffset(x)}
                                                    setCompetencies={comps => setFilteredCompetencies(comps)}
                                                    filteredMetric={filteredMetric}
                                                    setFilteredMetric={setFilteredMetric}
                                                />
                                            )}
                                            {integrationMetrics &&
                                                integrations
                                                    .filter(
                                                        i =>
                                                            i.data_Integration &&
                                                            (integrationMetrics.some(m => m.ref === i.ref) ||
                                                                realtimeMetrics.some(m => m.ref === i.ref))
                                                    )
                                                    .map(m => {
                                                        return currentMetric !== m.ref ? null : (
                                                            <IntegrationMetrics
                                                                setLastPaint={setLastPaint}
                                                                testId="121s-integration-metrics"
                                                                key={`metric-${m.ref}`}
                                                                integration={m.ref}
                                                                realtimeMetrics={realtimeMetrics}
                                                                loading={loadingInt}
                                                                currentMetric={currentMetric}
                                                                extraButtons={metricButtons}
                                                                extraMetrics={extraMetrics}
                                                                className="bottom-row"
                                                                metrics={integrationMetrics.filter(i => i.ref === m.ref)}
                                                                lastUpdated={m.lastUpdated}
                                                                profile={profile}
                                                                updateRange={x => setRange(x)}
                                                                updateOffset={x => setOffset(x)}
                                                                setCompetencies={comps => setFilteredCompetencies(comps)}
                                                                filteredMetric={filteredMetric}
                                                                setFilteredMetric={setFilteredMetric}
                                                            />
                                                        );
                                                    })}
                                        </>
                                    ) : (
                                        <Box title={SystemText('User121/CrmMetrics/Title')}>
                                            {SystemText(
                                                `User121/Competencies/NoMetrics/${isMe ? 'You' : 'Them'}`,
                                                profile.first_Name
                                            )}
                                        </Box>
                                    )}
                                </div>
                                <div data-testid="121-competencies" className="col-md-6">
                                    <Competencies
                                        setLastPaint={setLastPaint}
                                        user={user}
                                        profile={profile}
                                        benchmark={benchmark}
                                        filtered={filteredCompetencies}
                                        clearFilter={() => {
                                            setFilteredCompetencies(null);
                                            setFilteredMetric(null);
                                        }}
                                    />
                                </div>
                                {showNotesOnPerformanceDashboard && (
                                    <div data-testid="121s-notes" className="col-md-12">
                                        <Notes
                                            permissions={permissions}
                                            notes={notes}
                                            profile={profile}
                                            reload={loadOne2OneNotes}
                                            deleteNote={deleting => deleteNote(deleting)}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </section>
                    <EditGoals
                        show={showEditPersonalGoals}
                        onClose={() => setShowEditPersonalGoals(false)}
                        profile={profile}
                    />
                </div>
            )}
            <PredictiveModelWrapper
                show={showModel}
                onClose={() => setShowModel(false)}
                profile={profile}
                filterCompetencies={comps => {
                    setShowModel(false);
                    setFilteredCompetencies(comps);
                }}
            />
        </>
    );
};

export default withRouter(User121s);
